import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react';
import apiConfig from '../../config/apiConfig';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchForeignResource } from '../../apis/resources';
import { fetchEnum } from '../../apis/enum';
import { useParams } from 'react-router-dom';

import { useRaspStore } from '../../store/raspStore';
import { useJeemainViewModel } from "../../viewModels/useJeemainViewModel";
import { LoginContext } from '../../context/LoginContext';
import { apiFetch } from '../../apis/apiFetch';
import './FormStyles.css';
import { BTechApplicationFields } from '../../config/btechApplicationFields';
import { validate } from '../../utils/validator';
import { jeeMainApplicationRules } from '../../validators/applicantValidator';
import RenderImage from '../QueryManager/RenderImage/RenderImage';
import jee_main_rank from "../../images/jee_main_rank.png";
import jee_main_application_no from "../../images/jee_main_scorecard_applno.png";
import jee_main_nta_score from "../../images/jee_main_scorecard_ntascore.png";
import { useAdmissionDriveKind } from '../../hooks/useAdmissionDriveKind';
import { useStudentProgress } from '../../context/StudentProgressContext';


export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface JeeMainApplicationFormRef {
  getData: () => Record<string, unknown>;
  validate: () => Record<string, string>;
}

const F = BTechApplicationFields.jeemain;

export type CreateJeemainProps = {
  variant?: 'standalone' | 'application';
  /** application row fields for prepopulate */
  initialApplicationRow?: Record<string, any>;
};

const JeemainApplicationFields = forwardRef<
  JeeMainApplicationFormRef,
  { initialApplicationRow?: Record<string, any> }
>((props, ref) => {
  const { initialApplicationRow } = props;
  const [examtype, setExamtype] = useState('');
  const [rollno, setRollno] = useState('');
  const [score, setScore] = useState('');
  const [rank, setRank] = useState('');
  const [scorecard, setScorecard] = useState<File | string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState("");

  const [showEnrollmentNoHelp, setShowEnrollmentNoHelp] = useState(false);
  const [showScoreHelp, setShowScoreHelp] = useState(false);
  const [showRankHelp, setShowRankHelp] = useState(false);
  const [showScorecardHelp, setShowScorecardHelp] = useState(false);

  useEffect(() => {
    const row = initialApplicationRow;
    if (!row) return;
    setExamtype(row[F.examtype] || 'JEE Main');
    setRollno(row[F.rollno] || '');
    setScore(row[F.score] != null ? String(row[F.score]) : '');
    setRank(row[F.rank] || '');
    const sc = row[F.scorecard];
    setScorecard(sc instanceof File ? sc : (typeof sc === 'string' ? sc : null));
  }, [initialApplicationRow]);

  const buildPayload = (): Record<string, unknown> => ({
    [F.examtype]: examtype,
    [F.rollno]: rollno,
    [F.score]: score,
    [F.rank]: rank,
    [F.scorecard]: scorecard,
  });

  useImperativeHandle(ref, () => ({
    getData: () => buildPayload(),
    validate: () => {
      const payload = buildPayload();
      const v = validate(payload as any, jeeMainApplicationRules);
      setErrors(v);
      return v;
    },
  }));
  const { isDriveEditOpen, isDriveOpen } = useAdmissionDriveKind();
  const { isAppEdit, isAppSubmitted } = useStudentProgress();

  const isEnabled = isDriveOpen || !(isDriveEditOpen && isAppSubmitted) || isAppEdit 

  return (
    <div className="form-container">
      <h3 className="form-subtitle">JEE Main 2026</h3>
      <div className="form-grid">
        <div
          className="form-group"
          onMouseEnter={() => setShowEnrollmentNoHelp(true)}
          onMouseLeave={() => setShowEnrollmentNoHelp(false)}
          style={{ position: "relative" }}
        >
          <label className="form-label">JEE Main Application No. *</label>
          <input
            type="text"
            className={`form-input ${errors[F.rollno] ? 'input-error' : ''}`}
            value={rollno}
            disabled={!isEnabled}
            onChange={(e) => {
              setRollno(e.target.value);
              if (errors[F.rollno]) setErrors((prev) => ({ ...prev, [F.rollno]: '' }));
            }}
          />
          {errors[F.rollno] && <span className="error-text">{errors[F.rollno]}</span>}
          {showEnrollmentNoHelp && (
            <div className="jee-score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>JEE Main Application No.</strong> from your official scorecard.
              </div>

              <img
                src={jee_main_application_no}
                alt="Sample JEE Main Rank (CRL)"
                className="score-hover-image"
              />
            </div>
          )}
        </div>
        <div
          className="form-group"
          onMouseEnter={() => setShowScoreHelp(true)}
          onMouseLeave={() => setShowScoreHelp(false)}
          style={{ position: "relative" }}
        >
          <label className="form-label">JEE Main NTA Final Total Score (upto 7 decimal places) *</label>
          <input
            type="number"
            className={`form-input ${errors[F.score] ? 'input-error' : ''}`}
            value={score}
            disabled={!isEnabled}
            onChange={(e) => {
              setScore(e.target.value);
              if (errors[F.score]) setErrors((prev) => ({ ...prev, [F.score]: '' }));
            }}
          />
          {errors[F.score] && <span className="error-text">{errors[F.score]}</span>}
          {showScoreHelp && (
            <div className="jee-score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>JEE Main NTA Score</strong> from your official scorecard.
              </div>

              <img
                src={jee_main_nta_score}
                alt="Sample JEE Main Rank (CRL)"
                className="score-hover-image"
              />
            </div>
          )}
        </div>
        <div
          className="form-group"
          onMouseEnter={() => setShowRankHelp(true)}
          onMouseLeave={() => setShowRankHelp(false)}
          style={{ position: "relative" }}
        >
          <label className="form-label">JEE Main Rank (CRL) *</label>
          <input
            type="number"
            className={`form-input ${errors[F.rank] ? 'input-error' : ''}`}
            value={rank}
            disabled={!isEnabled}
            onChange={(e) => {
              setRank(e.target.value);
              if (errors[F.rank]) setErrors((prev) => ({ ...prev, [F.rank]: '' }));
            }}
          />
          {errors[F.rank] && <span className="error-text">{errors[F.rank]}</span>}
          {showRankHelp && (
            <div className="jee-score-hover-preview">
              <div className="score-hover-header">
                Please Enter the below <strong>JEE Main Rank (CRL)</strong> from your official scorecard.
              </div>

              <img
                src={jee_main_rank}
                alt="Sample JEE Main Rank (CRL)"
                className="score-hover-image"
              />
            </div>
          )}
        </div>
        <div
          className="form-group"
          onMouseEnter={() => setShowScorecardHelp(true)}
          onMouseLeave={() => setShowScorecardHelp(false)}
          style={{ gridColumn: "1 / -1", position: "relative" }}>
          <>
            <label className="form-label">JEE Main Scorecard *</label>

            <div className="file-upload-wrapper">
              <input
                className={`form-input file-input ${errors[F.scorecard] ? "input-error" : ""}`}
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                disabled={!isEnabled}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;

                  // ✅ File size validation (512 KB)
                  if (file && file.size > 512 * 1024) {
                    setPhotoError(
                      "Scorecard size must be less than 512 KB. Please choose a smaller file."
                    );
                    e.target.value = "";
                    return;
                  }

                  setPhotoError("");

                  // ✅ Update state
                  setScorecard(file);

                  // ✅ Generate preview
                  if (file) {
                    const objectUrl = URL.createObjectURL(file);
                    setPreview(objectUrl);
                  }

                  // ✅ Clear validation error
                  if (errors[F.scorecard]) {
                    setErrors((prev) => ({
                      ...prev,
                      [F.scorecard]: "",
                    }));
                  }
                }}
              />
            </div>

            {/* ✅ File size error */}
            {photoError && (
              <span
                style={{
                  color: "red",
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {photoError}
              </span>
            )}

            {/* ✅ Validation error */}
            {errors[F.scorecard] && (
              <span className="error-text">{errors[F.scorecard]}</span>
            )}

            {/* ✅ Helper text */}
            <small
              style={{
                color: "#6b7280",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Max size: 512 KB | Formats: JPG, JPEG, PNG, PDF
            </small>
          </>

          {/* ✅ Preview OR Existing file */}
          {preview ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginLeft: 100,
                marginTop: 10,
              }}
            >
              <img src={preview} alt="preview" style={{ maxWidth: "40%" }} />
            </div>
          ) : typeof scorecard === "string" && scorecard ? (
            <div>
              <small
                style={{
                  color: "#1d1e1f",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Previous Uploaded File:
              </small>

              <RenderImage photo={scorecard} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

JeemainApplicationFields.displayName = 'JeemainApplicationFields';

function CreateJeemain(props: CreateJeemainProps, ref: React.ForwardedRef<JeeMainApplicationFormRef>) {
  const { variant = 'standalone', initialApplicationRow } = props;

  if (variant === 'application') {
    return (
      <JeemainApplicationFields ref={ref} initialApplicationRow={initialApplicationRow} />
    );
  }

  return <JeemainStandalone />;
}

const JeemainStandalone = () => {
  const { user } = useContext(LoginContext);
  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const metadataUrl = apiConfig.getResourceMetaDataUrl("Jeemain");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
  } = useJeemainViewModel(user?.userId || "", appId);

  useEffect(() => {
    loadMetadata();
  }, []);

  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  useQuery({
    queryKey: ['resMetaData', 'JeemainCreate'],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter((field: any) => field.foreign);
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ['foreignData', field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(field.foreign, field.name, field.foreign_field);
        }
      }

      const enumFields = data[0].fieldValues.filter((field: any) => field.isEnum === true);
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ['enum', field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useEffect(() => {
    console.log("data to save", dataToSave);
  }, [dataToSave]);

  useQuery({
    queryKey: ['resourceData', 'JeemainCreate', localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      const activeDriveId = localStorage.getItem("activeDriveId");
      if (!activeDriveId) return [];

      const params = new URLSearchParams();
      params.append("queryId", "GET_ALL");

      const response = await apiFetch(
        `${apiConfig.getResourceUrl('Jeemain')}?` + params.toString(),
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return (data.resource || []).filter((r: any) => r.drive_id === activeDriveId);
    },
  });

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Exam Details</h3>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Exam Type *</label>
          <input
            type="text"
            className="form-input"
            name="examtype"
            required
            value={dataToSave["examtype"] || ""}
            placeholder="e.g., GATE, GRE, CAT"
            onChange={(e) => setDataToSave({ ...dataToSave, examtype: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Registration Number *</label>
          <input
            type="text"
            className="form-input"
            name="rollno"
            required
            value={dataToSave["rollno"] || ""}
            placeholder="Registration Number"
            onChange={(e) => setDataToSave({ ...dataToSave, rollno: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Score *</label>
          <input
            type="text"
            className="form-input"
            name="score"
            required
            value={dataToSave["score"] || ""}
            placeholder="Score"
            onChange={(e) => setDataToSave({ ...dataToSave, score: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Rank *</label>
          <input
            type="text"
            className="form-input"
            name="rank"
            required
            value={dataToSave["rank"] || ""}
            placeholder="All India Rank"
            onChange={(e) => setDataToSave({ ...dataToSave, rank: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Scorecard *</label>
          <div className="file-upload-wrapper">
            <input
              className="form-input file-input"
              type="file"
              name="scorecard"
              required
              onChange={(e) => setDataToSave({ ...dataToSave, scorecard: e.target.files?.[0] || null })}
            />
            <button type="button" className="upload-btn">Upload</button>
          </div>
        </div>
      </div>

    </div>
  );
};

const CreateJeemainExport = forwardRef(CreateJeemain);
CreateJeemainExport.displayName = 'CreateJeemain';

export default CreateJeemainExport;
